import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, Panel } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useHeads } from "@/hooks/useLookups";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, inr, label } from "@/lib/format";
import { DATE_KEYS, GROUPS } from "@/lib/orderFields";
import { InvoicePlanPanel } from "@/components/order/InvoicePlan";

const fieldLabel = (k) => GROUPS.flatMap((g) => g.fields).find(([x]) => x === k)?.[1] || k;
const Notes = ({ o }) => (o.notes_remark ? (
  <span data-testid="lm-notes-remark">{o.notes_remark}
    <details className="mt-1 text-xs text-slate-500"><summary className="cursor-pointer">Original import text</summary>{o.notes}</details>
  </span>
) : o.notes);
const show = (o, [k, , type]) => (k === "notes" ? <Notes o={o} /> : type === "date" ? fmtDate(o[k]) : type === "projection" ? o.projection_ref_no : o[k]);

export function DetailsTab({ order }) {
  return (
    <div className="space-y-4" data-testid="lm-details-tab">
      {GROUPS.slice(0, 2).map((g) => (
        <Panel key={g.id} title={g.title}>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">{g.fields.map((f) => <Field key={f[0]} k={f[1]} v={show(order, f)} />)}</div>
        </Panel>
      ))}
    </div>
  );
}

function DateDialog({ order, open, onOpenChange, onDone }) {
  const [f, setF] = useState({ field: "planned_date", value: "", reason: "" });
  const save = async () => {
    try { await api.post(`/orders/${order.id}/dates`, f); toast.success("Date changed"); onOpenChange(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lm-date-dialog">
        <DialogHeader><DialogTitle>Change a date (reason recorded)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Date</Label>
            <Select value={f.field} onValueChange={(v) => setF({ ...f, field: v, value: order[v] || "" })}>
              <SelectTrigger data-testid="lm-date-field"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_KEYS.map((k) => <SelectItem key={k} value={k}>{fieldLabel(k)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>New date</Label><Input type="date" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="lm-date-value" /></div>
          <div><Label>Reason {order.status !== "draft" && "*"}</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="lm-date-reason" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="lm-date-save">Save date</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlanTab({ order, reload }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  const live = !["closed", "cancelled"].includes(order.status);
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="lm-plan-tab">
      <Panel title="Dates" actions={can("write") && live && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="lm-change-date-btn"><CalendarClock className="h-4 w-4 mr-1" />Change a date</Button>}>
        <div className="grid grid-cols-2 gap-4">{GROUPS[2].fields.map((f) => <Field key={f[0]} k={f[1]} v={show(order, f)} />)}</div>
      </Panel>
      <Panel title="Date change history">
        <div className="space-y-2 text-xs">
          {order.date_history.map((h) => Object.entries(h.changes).map(([k, [a, b]]) => (
            <div key={h.id + k} className="border-b border-slate-100 pb-1" data-testid="lm-date-history-row"><b>{fieldLabel(k)}</b>: {fmtDate(a) || "—"} → {fmtDate(b) || "—"} · {h.note || "no reason"} · {h.by?.name} · {fmtDateTime(h.at)}</div>
          )))}
          {!order.date_history.length && <div className="text-slate-400">No date changes yet</div>}
        </div>
      </Panel>
      <InvoicePlanPanel order={order} reload={reload} />
      <DateDialog order={order} open={open} onOpenChange={setOpen} onDone={reload} />
    </div>
  );
}

const COST_COLS = [
  { key: "movement_no", label: "Movement", mono: true },
  { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
  { key: "transporter", label: "Transporter" },
  { key: "share_pct", label: "Share of trip", right: true, render: (r) => `${r.share_pct}%` },
  { key: "allocated", label: "Allocated to LM", right: true, render: (r) => inr(r.allocated) },
  { key: "invoice_status", label: "Transporter invoice", render: (r) => label(r.invoice_status) },
];

export function CostsTab({ order, onOpenMovement }) {
  const l = order.logistics;
  const heads = useHeads(Object.keys(l.by_head || {}));
  const cards = [["Budget (planned)", inr(l.budget)], ...heads.map((h) => [h.label, inr((l.by_head || {})[h.code] || 0)]), ["Total logistics", inr(l.total)], ["% of basic value", l.pct_of_value != null ? `${l.pct_of_value}%` : "—"]];
  return (
    <div data-testid="lm-costs-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-3">
        {cards.map(([k, v]) => (
          <div key={k} className="rounded border border-slate-200 bg-white p-3" data-testid={`lm-cost-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div><div className="font-mono text-sm mt-1">{v}</div></div>
        ))}
      </div>
      <p className="mb-2 text-xs text-slate-500">Costs are entered on the transport movement and split across the LMs on that trip. Budget is a plan only and is never counted as cost. Freight scope: <b>{order.freight_scope || "not set"}</b>.</p>
      <DataTable testid="lm-costs-table" columns={COST_COLS} rows={l.rows} rowId={(r) => r.movement_id} onRowClick={(r) => onOpenMovement(r.movement_id)} empty="No movements yet" />
    </div>
  );
}
